// Phone-side page that turns the phone's camera + mic into a webcam stream
// for a paired desktop session. Reached via QR scan from the desktop:
//
//   /mobile-camera/:sessionId
//
// Flow:
//   1. Read sessionId from URL.
//   2. Ask camera + mic permission (default: user-facing camera, 720p).
//   3. Create signaling client + WebRTC client; wait for the desktop's offer.
//   4. Send back answer + ICE candidates.
//   5. Provide controls: switch camera, mute, disconnect.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createSignaling } from 'Components/MobileCamera/firebaseSignaling';
import { createMobileClient } from 'Components/MobileCamera/WebRTCManager';
import { VIDEO_RECORDER_ENABLED } from '../../../Config/featureFlags';
import styles from './index.module.css';

const VIDEO_CONSTRAINTS = {
  width: { ideal: 1280, max: 1280 },
  height: { ideal: 720, max: 720 },
  frameRate: { ideal: 30, max: 30 },
};

const STAGE = {
  PERMISSION: 'permission',
  WAITING: 'waiting',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
};

// Inline SVG icons — replace emoji controls.
const Icon = {
  Switch: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10" />
      <path d="M20.49 15a9 9 0 01-14.85 3.36L1 14" />
    </svg>
  ),
  Mic: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M19 11a7 7 0 01-14 0" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  ),
  MicOff: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V5a3 3 0 00-5.94-.6" />
      <path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  ),
  Disconnect: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91" />
      <line x1="23" y1="1" x2="1" y2="23" />
    </svg>
  ),
  Record: () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="currentColor" />
    </svg>
  ),
  Stop: () => (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
    </svg>
  ),
  Skip: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="5 4 15 12 5 20 5 4" fill="currentColor" stroke="none" />
      <line x1="19" y1="5" x2="19" y2="19" />
    </svg>
  ),
  Camera: () => (
    <svg viewBox="0 0 24 24" width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  Warning: () => (
    <svg viewBox="0 0 24 24" width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  PhoneOff: () => (
    <svg viewBox="0 0 24 24" width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91" />
      <line x1="23" y1="1" x2="1" y2="23" />
    </svg>
  ),
};

// Map desktop phase enum → which recording-control button is the primary CTA.
const PHASE = {
  IDLE: 'idle',
  PREP: 'prep',
  RECORDING: 'recording',
  REVIEW: 'review',
};

// Standalone "feature unavailable" view rendered in place of the full
// page when VIDEO_RECORDER_ENABLED is false. Defined as its own component
// so it can declare its own hooks (or none) without affecting the parent.
const MobileCameraUnavailable = () => (
  <div
    style={{
      minHeight: '100dvh',
      background: '#0b0d12',
      color: 'rgba(255,255,255,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: 'Montserrat, sans-serif',
      textAlign: 'center',
    }}
  >
    <div style={{ maxWidth: 320 }}>
      <h2 style={{ margin: '0 0 10px', fontSize: '1.2rem', fontWeight: 600 }}>Not available</h2>
      <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
        This page isn’t active right now. Please return to the gift on your desktop.
      </p>
    </div>
  </div>
);

const MobileCameraPage = () => {
  // Feature gate — short-circuit to a sibling component so this page never
  // mounts its hooks (camera permission, signaling, WebRTC) when the
  // recorder feature is off. Because the flag is a module constant, React
  // sees the same component tree every render — no rules-of-hooks issues.
  if (!VIDEO_RECORDER_ENABLED) return <MobileCameraUnavailable />;

  const { sessionId } = useParams();
  const [stage, setStage] = useState(STAGE.PERMISSION);
  const [errorMsg, setErrorMsg] = useState('');
  const [facing, setFacing] = useState('user'); // 'user' | 'environment'
  const [muted, setMuted] = useState(false);
  const [streamReady, setStreamReady] = useState(false);
  // Mirror of desktop's recorder state, pushed via signaling.onControl.
  const [desktopState, setDesktopState] = useState({
    phase: PHASE.IDLE,
    currentSlot: 0,
    totalSlots: 5,
    subject: '',
    question: '',
    durationCap: 60,
    prepCountdown: 0,
    elapsedSeconds: 0,
    slotsStatus: [],
  });
  const desktopPhase = desktopState.phase;

  const videoRef = useRef(null);
  const localStreamRef = useRef(null);
  const signalingRef = useRef(null);
  const clientRef = useRef(null);

  const teardown = async () => {
    if (clientRef.current) {
      try { clientRef.current.stop(); } catch (e) { /* noop */ }
      clientRef.current = null;
    }
    if (signalingRef.current) {
      try { signalingRef.current.setStatus('closed'); } catch (e) { /* noop */ }
      try { await signalingRef.current.teardown(); } catch (e) { /* noop */ }
      signalingRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* noop */ } });
      localStreamRef.current = null;
    }
  };

  // Capture local camera + mic, attach to the video preview, set up RTC.
  const acquireAndConnect = async (preferredFacing) => {
    if (!sessionId) {
      setErrorMsg('Missing session id in URL.');
      setStage(STAGE.ERROR);
      return;
    }
    // Browsers hide `navigator.mediaDevices` entirely on insecure origins
    // (anything that isn't HTTPS or localhost). If we got here over HTTP
    // on a LAN IP, mediaDevices will be undefined and there's no JS-side
    // fix — the user needs HTTPS or the Chrome insecure-origin flag.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const isInsecure = !window.isSecureContext
        && window.location.hostname !== 'localhost'
        && window.location.hostname !== '127.0.0.1';
      setErrorMsg(
        isInsecure
          ? `This page is served over HTTP, so the browser disabled camera access. Two ways to test:
1) On Chrome, open chrome://flags/#unsafely-treat-insecure-origin-as-secure, add ${window.location.origin}, restart Chrome.
2) Or expose the dev server over HTTPS (e.g. ngrok) and reload from the HTTPS URL.`
          : 'Camera access is not supported by this browser. Try the latest Chrome, Safari, Firefox, or Edge.',
      );
      setStage(STAGE.ERROR);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { ...VIDEO_CONSTRAINTS, facingMode: preferredFacing || facing },
        audio: true,
      });
      localStreamRef.current = stream;
      setStreamReady(true);
      if (videoRef.current) videoRef.current.srcObject = stream;

      const signaling = createSignaling(sessionId);
      signalingRef.current = signaling;
      // The desktop already created the session row; we only need to read it.

      const client = createMobileClient({
        signaling,
        localStream: stream,
        onConnectionStateChange: (state) => {
          if (state === 'connected') {
            setStage(STAGE.CONNECTED);
            try { signaling.setStatus('connected'); } catch (e) { /* noop */ }
          } else if (state === 'connecting' || state === 'checking') {
            setStage(STAGE.CONNECTING);
          } else if (state === 'disconnected') {
            setStage(STAGE.DISCONNECTED);
          } else if (state === 'failed' || state === 'closed') {
            setStage(STAGE.DISCONNECTED);
          }
        },
      });
      clientRef.current = client;

      // Listen for desktop control messages: mute/unmute + full state mirror.
      signaling.onControl((msg) => {
        if (!msg) return;
        if (msg.command === 'mute') {
          client.setMicMuted(true);
          setMuted(true);
        } else if (msg.command === 'unmute') {
          client.setMicMuted(false);
          setMuted(false);
        } else if (msg.command === 'state' && msg.phase) {
          setDesktopState({
            phase: msg.phase,
            currentSlot: msg.currentSlot ?? 0,
            totalSlots: msg.totalSlots ?? 0,
            subject: msg.subject || '',
            question: msg.question || '',
            durationCap: msg.durationCap ?? 60,
            prepCountdown: msg.prepCountdown ?? 0,
            elapsedSeconds: msg.elapsedSeconds ?? 0,
            slotsStatus: Array.isArray(msg.slotsStatus) ? msg.slotsStatus : [],
          });
        }
      });

      setStage(STAGE.WAITING);
    } catch (err) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Camera & microphone permission was blocked. Please allow access in your browser settings to continue.'
        : err?.message || 'Could not access camera or microphone.';
      setErrorMsg(msg);
      setStage(STAGE.ERROR);
    }
  };

  // Switch front <-> back camera. Replace the video track on the existing
  // RTCRtpSender so the desktop sees the new feed without renegotiation.
  const switchCamera = async () => {
    const next = facing === 'user' ? 'environment' : 'user';
    setFacing(next);
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { ...VIDEO_CONSTRAINTS, facingMode: next },
        audio: false, // keep existing audio track on the PC
      });
      const newVideoTrack = newStream.getVideoTracks()[0];
      if (clientRef.current && newVideoTrack) {
        await clientRef.current.replaceVideoTrack(newVideoTrack);
      }
      // Swap the track on the local preview stream + stop the old video track
      const localStream = localStreamRef.current;
      if (localStream) {
        const oldVideoTrack = localStream.getVideoTracks()[0];
        if (oldVideoTrack) {
          localStream.removeTrack(oldVideoTrack);
          try { oldVideoTrack.stop(); } catch (e) { /* noop */ }
        }
        localStream.addTrack(newVideoTrack);
        if (videoRef.current) videoRef.current.srcObject = localStream;
      }
    } catch (err) {
      // Reset to whatever was working
      setFacing(facing);
    }
  };

  // Send a recording-control command back to the desktop.
  const sendDesktopCommand = (command) => {
    if (!signalingRef.current) return;
    try { signalingRef.current.sendMobileControl(command); } catch (e) { /* noop */ }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (clientRef.current) clientRef.current.setMicMuted(next);
  };

  const handleDisconnect = async () => {
    await teardown();
    setStage(STAGE.DISCONNECTED);
  };

  // The <video> element only mounts once stage transitions away from the
  // PERMISSION/ERROR cards. By that point we may have already grabbed the
  // local stream, so the in-place srcObject assignment in acquireAndConnect
  // hits a null ref. Re-attach whenever the live stage renders a fresh
  // video element.
  useEffect(() => {
    const live = stage === STAGE.WAITING || stage === STAGE.CONNECTING || stage === STAGE.CONNECTED;
    if (!live) return;
    const v = videoRef.current;
    if (v && localStreamRef.current && v.srcObject !== localStreamRef.current) {
      try { v.srcObject = localStreamRef.current; } catch (e) { /* noop */ }
    }
  }, [stage, streamReady, facing]);

  // Keep the phone screen awake while we're streaming. Without this, the
  // OS sleeps after the user's idle timeout (often ~30s), pausing the
  // camera tracks and forcing WebRTC into `disconnected`. Wake Lock is
  // an async, best-effort API — silently no-op if unavailable (older
  // browsers / iOS Safari without HTTPS).
  useEffect(() => {
    if (stage !== STAGE.CONNECTED) return undefined;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return undefined;
    let lock = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        // eslint-disable-next-line no-undef
        lock = await navigator.wakeLock.request('screen');
      } catch (e) { /* noop — user may have denied or the API is gated */ }
    };
    acquire();
    // Re-acquire when the page becomes visible again — Wake Lock auto-
    // releases on tab switch / app background, so we have to ask again.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !cancelled) acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try { if (lock) lock.release(); } catch (e) { /* noop */ }
    };
  }, [stage]);

  // When the page comes back from the background (user switched apps and
  // returned), the camera tracks may have been muted by the OS. Force a
  // refresh on the local <video> element so playback resumes; the WebRTC
  // ICE-restart logic in WebRTCManager handles connection-level recovery.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const v = videoRef.current;
      if (v && v.paused && localStreamRef.current) {
        try {
          v.srcObject = localStreamRef.current;
          v.play().catch(() => { /* autoplay restrictions — user can tap to resume */ });
        } catch (e) { /* noop */ }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Cleanup on unmount + unload
  useEffect(() => {
    const onUnload = () => { teardown(); };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      teardown();
    };
    // eslint-disable-next-line
  }, []);

  // ── Render ──
  if (stage === STAGE.PERMISSION) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.emojiIcon}><Icon.Camera /></div>
          <h1 className={styles.title}>Use phone as camera</h1>
          <p className={styles.subtitle}>
            We&apos;ll stream your phone&apos;s camera and microphone to your desktop.
            No video is uploaded — it&apos;s a live feed for recording on the desktop.
          </p>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => acquireAndConnect()}
          >
            Start camera
          </button>
        </div>
      </div>
    );
  }

  if (stage === STAGE.ERROR) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.emojiIcon}><Icon.Warning /></div>
          <h1 className={styles.title}>Couldn&apos;t start camera</h1>
          <p className={styles.subtitle}>{errorMsg}</p>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => { setErrorMsg(''); setStage(STAGE.PERMISSION); }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (stage === STAGE.DISCONNECTED) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.emojiIcon}><Icon.PhoneOff /></div>
          <h1 className={styles.title}>Disconnected</h1>
          <p className={styles.subtitle}>The session has ended. You can reconnect from the desktop.</p>
        </div>
      </div>
    );
  }

  // WAITING / CONNECTING / CONNECTED — same layout, status badge differs
  const statusLabel = stage === STAGE.CONNECTED
    ? 'Connected to desktop'
    : stage === STAGE.CONNECTING
      ? 'Connecting…'
      : 'Waiting for desktop…';

  // Helpers for the slot dots row + countdown / timer overlays.
  const fmtTime = (sec) => {
    const m = Math.floor((sec || 0) / 60);
    const s = Math.floor((sec || 0) % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const showSlotStrip = stage === STAGE.CONNECTED && desktopState.totalSlots > 0;
  const showPromptPanel = stage === STAGE.CONNECTED
    && (desktopPhase === PHASE.PREP || desktopPhase === PHASE.RECORDING);
  const showRecBadge = stage === STAGE.CONNECTED && desktopPhase === PHASE.RECORDING;
  const showPrepNumber = stage === STAGE.CONNECTED
    && desktopPhase === PHASE.PREP
    && desktopState.prepCountdown > 0;

  return (
    <div className={styles.pageLive}>
      <div className={styles.preview}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          className={styles.previewVideo}
          autoPlay
          muted
          playsInline
        />

        {/* Slot strip — small dots at the top, one per question. Mirrors the
            desktop's slot column. Each dot color reflects status. */}
        {showSlotStrip && (
          <div className={styles.slotStrip}>
            {Array.from({ length: desktopState.totalSlots }, (_, i) => {
              const status = desktopState.slotsStatus[i] || 'empty';
              const isCurrent = i === desktopState.currentSlot
                && (desktopPhase === PHASE.PREP || desktopPhase === PHASE.RECORDING);
              const cls = [
                styles.slotDot,
                status === 'done' && styles.slotDotDone,
                status === 'skipped' && styles.slotDotSkipped,
                isCurrent && styles.slotDotCurrent,
              ].filter(Boolean).join(' ');
              return <span key={i} className={cls} aria-hidden="true" />;
            })}
          </div>
        )}

        {/* Top-right REC + timer pill */}
        {showRecBadge && (
          <div className={styles.timerBadge}>
            <span className={styles.timerDot} aria-hidden="true" />
            <span className={styles.timerLabel}>REC</span>
            <span className={styles.timerElapsed}>{fmtTime(desktopState.elapsedSeconds)}</span>
            <span className={styles.timerCap}>{`/ ${fmtTime(desktopState.durationCap)}`}</span>
          </div>
        )}

        {/* Centered prep countdown */}
        {showPrepNumber && (
          <div className={styles.prepOverlay}>
            <span className={styles.prepLabel}>Get ready…</span>
            <span className={styles.prepNumber}>{desktopState.prepCountdown}</span>
          </div>
        )}

        {/* Question prompt panel — slot N of M + the question text */}
        {showPromptPanel && (
          <div className={styles.promptPanel}>
            <div className={styles.promptCount}>
              {`Question ${desktopState.currentSlot + 1} of ${desktopState.totalSlots}`}
            </div>
            <p className={styles.promptText}>{desktopState.question || desktopState.subject}</p>
          </div>
        )}

        {/* Hide the connection-status pill once we're fully connected — the
            slot strip already conveys "we're live", and we want the timer
            pill in the top-right unobscured. */}
        {stage !== STAGE.CONNECTED && (
          <div className={styles.statusBadge}>
            <span className={styles.spinner} aria-hidden="true" />
            <span>{statusLabel}</span>
          </div>
        )}
      </div>

      {/* Recording controls — proxy to desktop's recorder via signaling.
          Visibility mirrors the desktop's state machine exactly:
            IDLE        → Start  + Skip
            PREP        → Skip   + Re-record
            RECORDING   → Skip   + Re-record + Finish
            REVIEW      → (nothing — recording done)
      */}
      {stage === STAGE.CONNECTED && desktopPhase !== PHASE.REVIEW && (
        <div className={styles.recordControls}>
          {desktopPhase === PHASE.IDLE && (
            <button
              type="button"
              className={`${styles.recBtn} ${styles.recBtnStart}`}
              onClick={() => sendDesktopCommand('startFlow')}
              aria-label="Start recording"
            >
              <Icon.Record />
              <span>Start</span>
            </button>
          )}
          <button
            type="button"
            className={styles.recBtn}
            onClick={() => sendDesktopCommand('deleteQuestion')}
            aria-label="Delete question"
          >
            <Icon.Skip />
            <span>Delete</span>
          </button>
          {(desktopPhase === PHASE.PREP || desktopPhase === PHASE.RECORDING) && (
            <button
              type="button"
              className={styles.recBtn}
              onClick={() => sendDesktopCommand('rerecord')}
              aria-label="Re-record"
            >
              <Icon.Switch />
              <span>Re-record</span>
            </button>
          )}
          {desktopPhase === PHASE.RECORDING && (
            <button
              type="button"
              className={`${styles.recBtn} ${styles.recBtnFinish}`}
              onClick={() => sendDesktopCommand('finishRecording')}
              aria-label="Finish recording"
            >
              <Icon.Stop />
              <span>Finish</span>
            </button>
          )}
        </div>
      )}

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.controlBtn}
          onClick={switchCamera}
          disabled={!streamReady}
          aria-label="Switch camera"
        >
          <span className={styles.controlIcon}><Icon.Switch /></span>
          <span className={styles.controlLabel}>Switch</span>
        </button>
        <button
          type="button"
          className={`${styles.controlBtn} ${muted ? styles.controlBtnActive : ''}`}
          onClick={toggleMute}
          disabled={!streamReady}
          aria-label={muted ? 'Unmute mic' : 'Mute mic'}
        >
          <span className={styles.controlIcon}>{muted ? <Icon.MicOff /> : <Icon.Mic />}</span>
          <span className={styles.controlLabel}>{muted ? 'Unmute' : 'Mute'}</span>
        </button>
        <button
          type="button"
          className={`${styles.controlBtn} ${styles.controlBtnDanger}`}
          onClick={handleDisconnect}
          aria-label="Disconnect"
        >
          <span className={styles.controlIcon}><Icon.Disconnect /></span>
          <span className={styles.controlLabel}>Disconnect</span>
        </button>
      </div>
    </div>
  );
};

export default MobileCameraPage;
